/**
 * OSStatsPage v2 — RunAI OS · Premium Dashboard

 * Design: Full OS-like layout · AskAI command bar top · Agent Chat panel
 * Real API data only. No mocks.
 */
import {
    Activity, Bot, Brain, Briefcase, ChevronRight,
    Code2, DollarSign, FlaskConical, Key, Maximize2,
    MessageSquare, Plus, Send, Share2,
    Sparkles, Terminal, TrendingUp, Users, Zap
} from 'lucide-react'
import { Suspense, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import BabaWorkPanel from '../../components/os/BabaWorkPanel'
import { useWindowManager } from '../../components/os/WindowManager/WindowManagerContext'
import { WINDOW_REGISTRY } from '../../components/os/WindowManager/windowRegistry'
import { API_URL, apiFetch, apiFetchJSON } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'

// ─── Types ────────────────────────────────────────────────────────────────────
interface FlzBalance { balance: number; total_earned: number; total_spent: number }
interface Agent { id: number; agent_name: string; status: string; tasks_completed?: number; expertise?: string }
interface Skill { id: number; name: string; category: string; enabled: boolean; color?: string; icon_name?: string }
interface ActivityItem { id: number; type: string; description: string; created_at: string; earned_flz?: number }
interface ChatBubble { role: 'user' | 'assistant'; content: string; agentName?: string; ts: string }

const statusColor = (s: string) => {
  if (s === 'active' || s === 'running') {return '#10b981'}
  if (s === 'idle' || s === 'pending') {return '#f59e0b'}
  return 'var(--secondary)'
}

const agentHue = (id: number) => `hsl(${(id * 47) % 360},65%,48%)`

function now() { return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }

// ─── AskAI Command Bar ────────────────────────────────────────────────────────
function AskAIBar({ openChatWindow }: {
  openChatWindow: () => void
}) {
  const [input, setInput] = useState('')
  const [thinking, setThinking] = useState(false)
  const [result, setResult] = useState('')
  const ref = useRef<HTMLInputElement>(null)

  const send = async () => {
    const text = input.trim()
    if (!text) {return}
    setInput('')
    setThinking(true)
    setResult('')
    try {
      const res = await apiFetch(
        `${API_URL}/api/chat/send`,
        { method: 'POST', body: JSON.stringify({ agent_id: 1, message: text }), headers: { 'Content-Type': 'application/json' } }
      )
      setResult((await res.json() as { response?: string }).response ?? '')
    } catch {
      setResult('Connection failed. Please try again.')
    }
    setThinking(false)
  }

  const suggestions = [
    'What can my agents do?',
    'Show my FLZ balance',
    'Start a research project',
    'Run Twitter scraper',
  ]

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(168,85,247,0.06) 100%)',
      border: '1px solid rgba(99,102,241,0.25)',
      borderRadius: 16,
      padding: '18px 20px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Glow accent */}
      <div style={{
        position: 'absolute', top: -40, right: -40,
        width: 140, height: 140, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: 'linear-gradient(135deg, #6366f1, #a855f7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
        }}>
          <Sparkles size={14} color="#fff" />
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--os-text)', letterSpacing: '-0.3px' }}>
          Ask RunAI
        </span>
        <span style={{
          marginLeft: 4, fontSize: 10, padding: '2px 7px', borderRadius: 20,
          background: 'rgba(99,102,241,0.15)', color: '#818cf8',
          border: '1px solid rgba(99,102,241,0.2)', fontWeight: 600,
        }}>
          AI OS
        </span>
        <button
          onClick={openChatWindow}
          title="Open full chat"
          style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5,
            padding: '4px 10px', borderRadius: 7, border: '1px solid var(--os-border)',
            background: 'var(--os-surface)', cursor: 'pointer',
            color: 'var(--os-muted)', fontSize: 11,
          }}
        >
          <Maximize2 size={11} />
          Full Chat
        </button>
      </div>

      {/* Input */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'var(--os-input-bg)',
        border: '1px solid var(--os-border)',
        borderRadius: 12, padding: '10px 14px',
        transition: 'border-color 0.2s',
      }}
        onFocus={() => { }}
      >
        <Terminal size={15} color="#6366f1" style={{ flexShrink: 0 }} />
        <input
          ref={ref}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && void send()}
          placeholder="Ask anything — run skills, chat with agents, manage projects..."
          style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            color: 'var(--os-text)', fontSize: 13, fontFamily: 'inherit',
          }}
        />
        {thinking && (
          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 4, height: 4, borderRadius: '50%', background: '#6366f1',
                animation: `bounce 1s ${i * 0.2}s infinite`,
              }} />
            ))}
          </div>
        )}
        <button
          onClick={() => void send()}
          disabled={!input.trim() || thinking}
          style={{
            width: 32, height: 32, borderRadius: 9, border: 'none',
            background: input.trim() && !thinking ? 'linear-gradient(135deg, #6366f1, #a855f7)' : 'var(--os-surface)',
            color: input.trim() && !thinking ? '#fff' : 'var(--os-dim)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: input.trim() && !thinking ? 'pointer' : 'default',
            boxShadow: input.trim() && !thinking ? '0 4px 12px rgba(99,102,241,0.35)' : 'none',
            transition: 'all 0.2s', flexShrink: 0,
          }}
        >
          <Send size={14} />
        </button>
      </div>

      {/* AI Response */}
      {result && (
        <div style={{
          marginTop: 12,
          padding: '12px 14px',
          background: 'rgba(16,185,129,0.06)',
          border: '1px solid rgba(16,185,129,0.2)',
          borderRadius: 10,
          fontSize: 12, lineHeight: 1.6,
          color: 'var(--os-text)',
          animation: 'winIn 0.2s ease-out',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <Brain size={12} color="#10b981" />
            <span style={{ fontSize: 10, color: '#10b981', fontWeight: 600 }}>RunAI Response</span>
          </div>
          {result}
        </div>
      )}

      {/* Suggestions */}
      {!result && !thinking && (
        <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
          {suggestions.map(s => (
            <button
              key={s}
              onClick={() => { setInput(s); ref.current?.focus() }}
              style={{
                padding: '4px 10px', borderRadius: 20,
                border: '1px solid var(--os-border)',
                background: 'var(--os-surface)',
                color: 'var(--os-muted)', fontSize: 10.5,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)'
                e.currentTarget.style.color = 'var(--os-text)'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--os-border)'
                e.currentTarget.style.color = 'var(--os-muted)'
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Agent Chat Panel ─────────────────────────────────────────────────────────
function AgentChatPanel({ agents, navigate }: { agents: Agent[]; navigate: (to: string) => void }) {
  const [activeAgent, setActiveAgent] = useState<Agent | null>(null)
  const [input, setInput] = useState('')
  const [chat, setChat] = useState<ChatBubble[]>([])
  const [sending, setSending] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (agents.length > 0 && !activeAgent) {
      const first = agents[0]
      setActiveAgent(first)
      setChat([{
        role: 'assistant',
        content: `Hello! I'm ${first.agent_name}. How can I help you today?`,
        agentName: first.agent_name,
        ts: now(),
      }])
    }
  }, [agents, activeAgent])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chat])

  const switchAgent = (a: Agent) => {
    setActiveAgent(a)
    setChat([{
      role: 'assistant',
      content: `Hi! I'm ${a.agent_name}${a.expertise ? `, specialized in ${a.expertise}` : ''}. What would you like to work on?`,
      agentName: a.agent_name,
      ts: now(),
    }])
    setInput('')
    inputRef.current?.focus()
  }

  const send = async () => {
    const text = input.trim()
    if (!text || !activeAgent || sending) {return}
    setInput('')
    setSending(true)
    setChat(prev => [...prev, { role: 'user', content: text, ts: now() }])
    try {
      const res = await apiFetch(
        `${API_URL}/api/chat/send`,
        { method: 'POST', body: JSON.stringify({ agent_id: activeAgent.id, message: text }), headers: { 'Content-Type': 'application/json' } }
      )
      const reply = (await res.json() as { response?: string }).response ?? ''
      setChat(prev => [...prev, {
        role: 'assistant', content: reply,
        agentName: activeAgent.agent_name, ts: now(),
      }])
    } catch {
      setChat(prev => [...prev, {
        role: 'assistant', content: 'Connection error. Please retry.',
        agentName: activeAgent.agent_name, ts: now(),
      }])
    }
    setSending(false)
  }

  if (agents.length === 0) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: 220, gap: 10,
        background: 'var(--os-win-bg)', border: '1px solid var(--os-border)', borderRadius: 14,
      }}>
        <Bot size={28} color="var(--os-muted)" />
        <div style={{ fontSize: 12, color: 'var(--os-muted)' }}>No agents yet</div>
        <Link to="/dashboard/agents/add" style={{
          fontSize: 12, color: '#6366f1', textDecoration: 'none',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <Plus size={12} /> Create your first agent
        </Link>
      </div>
    )
  }

  return (
    <div style={{
      background: 'var(--os-win-bg)',
      border: '1px solid var(--os-border)',
      borderRadius: 14, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Panel header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: '1px solid var(--os-border)',
        background: 'rgba(255,255,255,0.02)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <MessageSquare size={13} color="#8b5cf6" />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--os-text)' }}>Agent Chat</span>
          {activeAgent && (
            <span style={{
              fontSize: 10, padding: '1px 7px', borderRadius: 12,
              background: `${agentHue(activeAgent.id)}20`,
              color: agentHue(activeAgent.id),
              border: `1px solid ${agentHue(activeAgent.id)}40`,
              fontWeight: 600,
            }}>
              {activeAgent.agent_name}
            </span>
          )}
        </div>
        <button
          onClick={() => activeAgent && navigate(`/aicloud/app/chat?agent=${activeAgent.id}`)}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '3px 9px', borderRadius: 6,
            border: '1px solid var(--os-border)', background: 'var(--os-surface)',
            cursor: 'pointer', color: 'var(--os-muted)', fontSize: 10,
          }}
        >
          <Maximize2 size={10} />
          Expand
        </button>
      </div>

      {/* Agent selector tabs */}
      <div style={{
        display: 'flex', gap: 0, overflowX: 'auto', padding: '8px 12px',
        borderBottom: '1px solid var(--os-border)',
        scrollbarWidth: 'thin',
      }}>
        {agents.map(a => (
          <button
            key={a.id}
            onClick={() => switchAgent(a)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 10px', borderRadius: 8, flexShrink: 0,
              border: activeAgent?.id === a.id ? `1px solid ${agentHue(a.id)}50` : '1px solid transparent',
              background: activeAgent?.id === a.id ? `${agentHue(a.id)}15` : 'transparent',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            {/* Avatar */}
            <div style={{
              width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
              background: agentHue(a.id),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9, fontWeight: 700, color: '#fff',
            }}>
              {a.agent_name.charAt(0)}
            </div>
            <span style={{
              fontSize: 11, fontWeight: activeAgent?.id === a.id ? 600 : 400,
              color: activeAgent?.id === a.id ? 'var(--os-text)' : 'var(--os-muted)',
              whiteSpace: 'nowrap',
            }}>
              {a.agent_name}
            </span>
            {/* Online dot */}
            <div style={{
              width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
              background: statusColor(a.status),
            }} />
          </button>
        ))}
        {agents.length > 8 && (
          <Link to="/aicloud/app/chat" style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '5px 10px', borderRadius: 8, flexShrink: 0,
            color: '#6366f1', textDecoration: 'none', fontSize: 11,
          }}>
            +{agents.length - 8} more →
          </Link>
        )}
      </div>

      {/* Chat messages */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '12px 14px',
        display: 'flex', flexDirection: 'column', gap: 8,
        maxHeight: 220, minHeight: 160,
        scrollbarWidth: 'thin',
      }}>
        {chat.map((b, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: b.role === 'user' ? 'flex-end' : 'flex-start',
              gap: 7, alignItems: 'flex-end',
            }}
          >
            {b.role === 'assistant' && activeAgent && (
              <div style={{
                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                background: agentHue(activeAgent.id),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 700, color: '#fff',
              }}>
                {activeAgent.agent_name.charAt(0)}
              </div>
            )}
            <div style={{ maxWidth: '72%' }}>
              <div style={{
                padding: '8px 12px', borderRadius: b.role === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
                background: b.role === 'user'
                  ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                  : 'var(--os-surface)',
                border: b.role === 'user' ? 'none' : '1px solid var(--os-border)',
                fontSize: 12, lineHeight: 1.5,
                color: b.role === 'user' ? '#fff' : 'var(--os-text)',
                boxShadow: b.role === 'user' ? '0 4px 12px rgba(99,102,241,0.25)' : 'none',
              }}>
                {b.content}
              </div>
              <div style={{
                fontSize: 9.5, color: 'var(--os-dim)',
                marginTop: 3,
                textAlign: b.role === 'user' ? 'right' : 'left',
                paddingLeft: b.role === 'assistant' ? 4 : 0,
              }}>
                {b.ts}
              </div>
            </div>
          </div>
        ))}
        {sending && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            {activeAgent && (
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: agentHue(activeAgent.id),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 700, color: '#fff',
              }}>
                {activeAgent.agent_name.charAt(0)}
              </div>
            )}
            <div style={{
              padding: '8px 14px', borderRadius: '4px 12px 12px 12px',
              background: 'var(--os-surface)', border: '1px solid var(--os-border)',
              display: 'flex', gap: 4, alignItems: 'center',
            }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: 5, height: 5, borderRadius: '50%', background: '#6366f1',
                  animation: `bounce 1s ${i * 0.2}s infinite`,
                }} />
              ))}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Chat input */}
      <div style={{
        padding: '10px 14px',
        borderTop: '1px solid var(--os-border)',
        display: 'flex', gap: 8, alignItems: 'center',
        background: 'rgba(255,255,255,0.01)',
      }}>
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && void send()}
          placeholder={activeAgent ? `Message ${activeAgent.agent_name}...` : 'Select an agent...'}
          style={{
            flex: 1, padding: '8px 12px',
            background: 'var(--os-input-bg)',
            border: '1px solid var(--os-border)',
            borderRadius: 9, color: 'var(--os-text)', fontSize: 12, outline: 'none',
            transition: 'border-color 0.2s',
          }}
          onFocus={e => { e.target.style.borderColor = 'rgba(99,102,241,0.45)' }}
          onBlur={e => { e.target.style.borderColor = 'var(--os-border)' }}
        />
        <button
          onClick={() => void send()}
          disabled={!input.trim() || sending}
          style={{
            width: 34, height: 34, borderRadius: 9, border: 'none',
            background: input.trim() && !sending
              ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
              : 'var(--os-surface)',
            color: input.trim() && !sending ? '#fff' : 'var(--os-dim)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: input.trim() && !sending ? 'pointer' : 'default',
            boxShadow: input.trim() && !sending ? '0 4px 12px rgba(99,102,241,0.3)' : 'none',
            transition: 'all 0.2s', flexShrink: 0,
          }}
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  )
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon, color, sub, to }: {
  label: string; value: string | number; icon: React.ReactNode
  color: string; sub?: string; to: string
}) {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <div style={{
        background: 'var(--os-win-bg)',
        border: '1px solid var(--os-border)',
        borderRadius: 12, padding: '14px 16px',
        transition: 'all 0.18s', cursor: 'pointer',
      }}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = `${color}60`
          e.currentTarget.style.transform = 'translateY(-1px)'
          e.currentTarget.style.boxShadow = `0 6px 20px ${color}15`
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = 'var(--os-border)'
          e.currentTarget.style.transform = 'none'
          e.currentTarget.style.boxShadow = 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: `${color}18`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {icon}
          </div>
          <ChevronRight size={12} color="var(--os-dim)" />
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color, letterSpacing: '-0.5px', lineHeight: 1 }}>
          {value}
        </div>
        <div style={{ fontSize: 11, color: 'var(--os-muted)', marginTop: 4 }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: 'var(--os-dim)', marginTop: 2 }}>{sub}</div>}
      </div>
    </Link>
  )
}

// ─── Skill App Icon (PC icon style) ──────────────────────────────────────────
function SkillAppIcon({ skill }: { skill: Skill }) {
  const [hover, setHover] = useState(false)
  const colors: Record<string, string> = {
    Development: '#3b82f6', Data: '#8b5cf6', Testing: '#10b981',
    DevOps: '#f59e0b', Productivity: '#ec4899', Research: '#06b6d4',
    Communication: '#84cc16', Security: '#ef4444', AI: '#a855f7',
  }
  const bg = skill.color ?? colors[skill.category] ?? '#6366f1'

  return (
    <Link to={`/aicloud/skills/${skill.id}`} style={{ textDecoration: 'none' }}>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
          padding: '10px 8px', borderRadius: 10, cursor: 'pointer',
          background: hover ? 'var(--os-icon-hover)' : 'transparent',
          transition: 'background 0.15s',
        }}
      >
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: `linear-gradient(135deg, ${bg}dd, ${bg}88)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 4px 12px ${bg}35`,
          transform: hover ? 'scale(1.08) translateY(-1px)' : 'scale(1)',
          transition: 'all 0.15s',
        }}>
          <Zap size={18} color="#fff" />
        </div>
        <span style={{
          fontSize: 10, color: 'var(--os-icon-label)',
          textAlign: 'center', lineHeight: 1.2,
          maxWidth: 58, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {skill.name}
        </span>
        {skill.enabled && (
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#10b981' }} />
        )}
      </div>
    </Link>
  )
}

// ─── Quick Launch Strip ───────────────────────────────────────────────────────
function QuickLaunch({ navigate }: { navigate: (to: string) => void }) {
  const items = [
    { label: 'Social', icon: <Users size={14} color="#fff" />, color: '#3b82f6', to: '/social' },
    { label: 'Chat', icon: <MessageSquare size={14} color="#fff" />, color: '#8b5cf6', to: '/aicloud/app/chat' },
    { label: 'Work', icon: <Briefcase size={14} color="#fff" />, color: '#10b981', to: '/dashboard/projects/work' },
    { label: 'Research', icon: <FlaskConical size={14} color="#fff" />, color: '#a855f7', to: '/dashboard/projects/research' },
    { label: 'Skills', icon: <Zap size={14} color="#fff" />, color: '#f59e0b', to: '/aicloud/skills' },
    { label: 'Scraper', icon: <Share2 size={14} color="#fff" />, color: '#ec4899', to: '/dashboard/work?cat=social_automation' },
    { label: 'API Keys', icon: <Key size={14} color="#fff" />, color: '#06b6d4', to: '/dashboard/platforms/api-keys' },
    { label: 'API Hub', icon: <Code2 size={14} color="#fff" />, color: 'var(--secondary)', to: '/dashboard/ai-platforms' },
  ]
  return (
    <div style={{
      background: 'var(--os-win-bg)',
      border: '1px solid var(--os-border)',
      borderRadius: 14, padding: '14px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Terminal size={13} color="#6b7280" />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--os-text)' }}>Quick Launch</span>
        </div>
        <span style={{ fontSize: 10, color: 'var(--os-muted)' }}>Click to open</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {items.map(item => (
          <button
            key={item.label}
            onClick={() => navigate(item.to)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
              padding: '10px 6px', borderRadius: 10,
              border: '1px solid var(--os-border)',
              background: 'var(--os-surface)',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = `${item.color}12`
              e.currentTarget.style.borderColor = `${item.color}50`
              e.currentTarget.style.transform = 'translateY(-1px)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'var(--os-surface)'
              e.currentTarget.style.borderColor = 'var(--os-border)'
              e.currentTarget.style.transform = 'none'
            }}
          >
            <div style={{
              width: 30, height: 30, borderRadius: 9,
              background: `linear-gradient(135deg, ${item.color}dd, ${item.color}99)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 3px 8px ${item.color}30`,
            }}>
              {item.icon}
            </div>
            <span style={{ fontSize: 10.5, color: 'var(--os-text)', fontWeight: 600 }}>
              {item.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── Activity Feed ────────────────────────────────────────────────────────────
function ActivityFeed({ items }: { items: ActivityItem[] }) {
  const typeColor: Record<string, string> = {
    skill_run: '#a855f7', chat: '#6366f1', project: '#3b82f6',
    work: '#10b981', research: '#8b5cf6', default: 'var(--secondary)',
  }
  return (
    <div style={{
      background: 'var(--os-win-bg)', border: '1px solid var(--os-border)',
      borderRadius: 14, padding: '14px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <Activity size={13} color="#6b7280" />
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--os-text)' }}>Recent Activity</span>
      </div>
      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '14px 0', fontSize: 12, color: 'var(--os-muted)' }}>
          No activity yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {items.slice(0, 7).map((a, i) => {
            const col = typeColor[a.type] ?? typeColor.default
            return (
              <div key={a.id ?? i} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 10px', borderRadius: 8,
                background: 'var(--os-surface)',
                border: '1px solid var(--os-border)',
              }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: col, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--os-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.description}
                  </div>
                </div>
                {a.earned_flz !== null && a.earned_flz !== undefined && a.earned_flz > 0 && (
                  <div style={{ fontSize: 11, color: '#10b981', fontWeight: 700, flexShrink: 0 }}>
                    +{a.earned_flz.toFixed(2)}
                  </div>
                )}
                <div style={{ fontSize: 9.5, color: 'var(--os-dim)', flexShrink: 0 }}>
                  {new Date(a.created_at).toLocaleDateString()}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function OSStatsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { openWindow, windows } = useWindowManager()

  const [flz, setFlz] = useState<FlzBalance>({ balance: 0, total_earned: 0, total_spent: 0 })
  const [agents, setAgents] = useState<Agent[]>([])
  const [skills, setSkills] = useState<Skill[]>([])
  const [activity, setActivity] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)
  const [showPackageModal, setShowPackageModal] = useState(!(user?.package && user.package !== 'free'))
  const [selectedPackage, setSelectedPackage] = useState<'free' | 'pro' | 'enterprise'>('pro')

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      const [flzRes, agentsRes, skillsRes, activityRes] = await Promise.allSettled([
        apiFetchJSON<FlzBalance>('/api/flz/balance'),
        apiFetchJSON<Agent[] | { agents: Agent[] }>('/api/agents'),
        apiFetchJSON<Skill[] | { skills: Skill[] }>('/api/aicloud/skills/installed'),
        apiFetchJSON<ActivityItem[] | { activities: ActivityItem[] }>('/api/users/me/activity'),
      ])
      if (flzRes.status === 'fulfilled') {setFlz(flzRes.value)}
      if (agentsRes.status === 'fulfilled') {
        const v = agentsRes.value
        setAgents(Array.isArray(v) ? v : ((v as { agents?: Agent[] }).agents ?? []))
      }
      if (skillsRes.status === 'fulfilled') {
        const v = skillsRes.value
        setSkills(Array.isArray(v) ? v : ((v as { skills?: Skill[] }).skills ?? []))
      }
      if (activityRes.status === 'fulfilled') {
        const v = activityRes.value
        setActivity(Array.isArray(v) ? v : ((v as { activities?: ActivityItem[] }).activities ?? []))
      }
      setLoading(false)
    }
    void load()
  }, [])

  const openChatWindow = () => {
    const ChatComponent = WINDOW_REGISTRY['/dashboard/chat']
    if (!ChatComponent || windows.some(w => w.id === 'chat')) {return}
    const vw = window.innerWidth
    const vh = window.innerHeight
    const w = Math.min(700, Math.floor(vw * 0.80))
    const h = Math.min(580, Math.floor(vh * 0.80))
    const Wrapped = () => (
      <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: 'var(--os-muted)', fontSize: 13 }}>Loading...</div>}>
        <ChatComponent />
      </Suspense>
    )
    openWindow({ id: 'chat', title: 'Chat', component: Wrapped, position: { x: Math.floor((vw - w) / 2), y: Math.floor((vh - h) / 2) }, size: { width: w, height: h } })
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', gap: 5 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#6366f1', opacity: 0.6,
              animation: `bounce 1s ${i * 0.18}s infinite`,
            }} />
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--os-muted)' }}>Booting RunAI OS...</div>
      </div>
    )
  }

  const activeAgents = agents.filter(a => a.status === 'active' || a.status === 'running')

  return (
    <div style={{
      padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 12,
      color: 'var(--os-text)', fontFamily: 'inherit',
      overflowY: 'auto', height: '100%',
    }}>

      {/* ── Profile strip ────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: 'var(--os-win-bg)',
        border: '1px solid var(--os-border)',
        borderRadius: 14, padding: '12px 16px',
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: user?.avatar ? 'transparent' : 'linear-gradient(135deg,#6366f1,#ec4899)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, fontWeight: 800, color: '#fff', overflow: 'hidden',
          boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
        }}>
          {user?.avatar
            ? <img src={String(user.avatar)} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : (user?.display_name ?? user?.username ?? 'U').charAt(0).toUpperCase()
          }
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.3px' }}>
            {user?.display_name ?? user?.username ?? ''}
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--os-muted)', marginTop: 1 }}>
            @{user?.username ?? ''} · Lv {(user?.level as number | undefined) ?? 1} · {String(user?.role ?? 'user').toUpperCase()}
          </div>
        </div>
        {[
          { label: 'XP', value: (user?.total_xp ?? 0).toLocaleString(), color: '#8b5cf6' },
          { label: 'FLZ', value: flz.balance.toFixed(1), color: '#10b981' },
          { label: 'Agents', value: agents.length, color: '#6366f1' },
          { label: 'Skills', value: skills.length, color: '#f59e0b' },
        ].map(s => (
          <div key={s.label} style={{ textAlign: 'center', minWidth: 44 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 9.5, color: 'var(--os-muted)', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── AskAI Command Bar ─────────────────────────────── */}
      <AskAIBar openChatWindow={openChatWindow} />

      {/* ── Agent Hub + Social Feed ───────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, minHeight: 0 }}>
        <AgentChatPanel agents={agents} navigate={navigate} />
        {/* Social Feed Widget */}
        <div style={{
          background: 'var(--os-win-bg)', border: '1px solid var(--os-border)',
          borderRadius: 14, padding: '14px 16px',
          display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MessageSquare size={13} color="#ec4899" />
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--os-text)' }}>Social Feed</span>
          </div>
          <Link to="/social" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            padding: '20px 12px', borderRadius: 10, background: 'var(--os-surface)',
            border: '1px solid var(--os-border)', cursor: 'pointer',
            color: '#6366f1', textDecoration: 'none', fontSize: 13, fontWeight: 500,
            transition: 'all 0.2s',
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.12)'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--os-surface)'; e.currentTarget.style.borderColor = 'var(--os-border)' }}
          >
            <Sparkles size={14} /> Open Social Feed →
          </Link>
        </div>
      </div>

      {/* ── Research Projects Widget ─────────────────────── */}
      <Link to="/research" style={{ textDecoration: 'none' }}>
        <div style={{
          background: 'var(--os-win-bg)', border: '1px solid var(--os-border)',
          borderRadius: 14, padding: '14px 16px',
          cursor: 'pointer', transition: 'all 0.2s',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.6)' }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--os-border)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Brain size={18} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--os-text)' }}>Research Projects</div>
              <div style={{ fontSize: 10.5, color: 'var(--os-muted)', marginTop: 2 }}>Explore & collaborate on research</div>
            </div>
          </div>
          <ChevronRight size={16} color="var(--os-muted)" />
        </div>
      </Link>

      {/* ── Stat Cards row ───────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <StatCard label="FLZ Balance" value={`${flz.balance.toFixed(2)}`}
          icon={<DollarSign size={14} color="#10b981" />} color="#10b981"
          sub={`Earned ${flz.total_earned.toFixed(1)}`} to="/dashboard/wallet" />
        <StatCard label="Active Agents" value={activeAgents.length}
          icon={<Bot size={14} color="#8b5cf6" />} color="#8b5cf6"
          sub={`${agents.length} total`} to="/dashboard/agents" />
        <StatCard label="Installed Skills" value={skills.length}
          icon={<Zap size={14} color="#f59e0b" />} color="#f59e0b"
          sub="Tap to launch" to="/aicloud/skills" />
        <StatCard label="Earned Total" value={`${flz.total_earned.toFixed(1)}`}
          icon={<TrendingUp size={14} color="#3b82f6" />} color="#3b82f6"
          sub={`Spent ${flz.total_spent.toFixed(1)}`} to="/dashboard/wallet" />
      </div>

      {/* ── Baba Work Panel + Activity Feed ────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <BabaWorkPanel />
        <ActivityFeed items={activity} />
      </div>

      {/* ── Skills grid (app icons) + Quick Launch ─────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* Skills: app icon grid */}
        <div style={{
          background: 'var(--os-win-bg)', border: '1px solid var(--os-border)',
          borderRadius: 14, padding: '14px 16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Zap size={13} color="#f59e0b" />
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--os-text)' }}>Skills</span>
            </div>
            <Link to="/aicloud/skills" style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none' }}>
              Marketplace →
            </Link>
          </div>
          {skills.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '14px 0', fontSize: 12, color: 'var(--os-muted)' }}>
              No skills installed.{' '}
              <Link to="/aicloud/skills" style={{ color: '#6366f1', textDecoration: 'none' }}>Browse →</Link>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, 68px)',
              gap: 4,
            }}>
              {skills.slice(0, 8).map(s => <SkillAppIcon key={s.id} skill={s} />)}
            </div>
          )}
        </div>

        {/* Quick Launch grid */}
        <QuickLaunch navigate={navigate} />
      </div>

      {/* Package Selection Modal */}
      {showPackageModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 999
        }}>
          <div style={{
            background: 'var(--os-win-bg)', border: '1px solid var(--os-border)',
            borderRadius: 16, padding: '32px 40px', maxWidth: 500,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, marginBottom: 12 }}>Select Your Plan</h2>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--os-muted)', marginBottom: 24 }}>
              Choose a plan to unlock features and start earning
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              {[
                { id: 'free', name: 'Free', price: '$0', desc: 'Get started' },
                { id: 'pro', name: 'Pro', price: '$9/mo', desc: 'Most popular' },
                { id: 'enterprise', name: 'Enterprise', price: 'Custom', desc: 'For teams' }
              ].map(plan => (
                <button
                  key={plan.id}
                  onClick={() => setSelectedPackage(plan.id as any)}
                  style={{
                    padding: '16px 20px', borderRadius: 10,
                    border: selectedPackage === plan.id ? '2px solid #6366f1' : '1px solid var(--os-border)',
                    background: selectedPackage === plan.id ? 'rgba(99,102,241,0.1)' : 'transparent',
                    cursor: 'pointer', textAlign: 'left', color: 'var(--os-text)',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{plan.name}</div>
                  <div style={{ fontSize: 13, color: 'var(--os-muted)' }}>{plan.price} • {plan.desc}</div>
                </button>
              ))}
            </div>

            <button
              onClick={() => setShowPackageModal(false)}
              style={{
                width: '100%', padding: '12px 16px',
                background: '#6366f1', color: '#fff', border: 'none',
                borderRadius: 8, fontSize: 14, fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Continue with {selectedPackage.charAt(0).toUpperCase() + selectedPackage.slice(1)}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
